// React
import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect, useStore } from 'react-redux';
import { createSelector } from 'reselect';
import classNames from 'classnames';
import * as Rematrix from 'rematrix';
import { compose } from '../../node_module_clones/recompose';

// Utils
import dontUpdateForKeys from '../utils/milanoteRecompose/dontUpdateForKeys';
import { getShowBoardGrid } from './background/boardBackgroundUtils';
import measurementsRegistry from '../components/measurementsStore/measurementsRegistry';
import * as matrixLib from '../../common/maths/matrix/matrixUtils';
import { convertMatrixToCssTransform } from '../utils/cssUtil';
import { multiRef } from '../utils/multiRef';
import { isPlatformIframe } from '../platform/utils/platformDetailsUtils';
import { triggerBackgroundPulseCssAnimation } from '../scss/mixins/scssBackgroundPulseUtil';

// Actions
import { setFocusSection } from '../reducers/focusActions';
import { showGuestAppRegistrationForm } from '../reducers/guestNavigationActions';
import { setSelectedElements } from '../element/selection/selectionActions';
import { canGiveFeedback } from '../../common/permissions/permissionUtil';

// Selectors
import { getGridSize } from '../utils/grid/gridSizeSelector';
import { getPlatformDetailsSelector } from '../platform/platformSelector';
import { getCurrentVisibleBoardCanvasOrigin } from '../element/selectors/currentBoardSelector';
import {
    canvasZoomModeSelector,
    canvasZoomScaleSelector,
    canvasZoomTranslationPxPojoSelector,
} from './store/canvasZoomSelector';
import { getIsCurrentVisibleBoardFetched, getCurrentBoardInboxState } from '../element/board/boardSelector';
import { debugToolProperty } from '../debug/store/debugToolSelector';

// Hooks
import useIpadDoubleTapFix from './useIpadDoubleTapFix';
import { usePerformHapticFeedback } from '../utils/haptics/usePerformHapticFeedback';

// Components
import PoiBoardSectionContext from '../components/pointsOfInterest/PoiBoardSectionContext';
import grabScrollDecorator from './grabScrollDecorator';
import DocumentModeCanvasDebugger from '../debug/documentMode/DocumentModeCanvasDebugger';
import canvasLineEdgeDropTarget, { getLineEdgeCanvasDropPosition } from '../element/line/canvasLineEdgeDropTarget';
import canvasDropTargetDecorator from './dnd/canvasDropTargetDecorator';
import canvasDoubleClickShortcutDecorator from './canvasDoubleClickShortcutDecorator';
import canvasSizeDecorator from './canvasSizeDecorator';
import canvasReturnStateDecorator from './canvasReturnStateDecorator';
import SelectionMarquee from './selection/SelectionMarquee';
import selectionMarqueeCanvasStateDecorator from './selection/selectionMarqueeCanvasStateDecorator';
import CanvasScaleManager from './scale/CanvasScaleManager';
import CanvasElementOrchestrator from './CanvasElementOrchestrator';
import BodyBackgroundColor from './BodyBackgroundColor';
import ZoomCanvas from './zoom/ZoomCanvas';
import CanvasScrollArea from './CanvasScrollArea';
import { zoomStateContextConsumer, zoomStateContextProviderDecorator } from './zoom/ZoomStateContext';
import LiveDrawingCanvasRenderer from '../element/drawing/liveDrawing/LiveDrawingCanvasRenderer';
import LiveCollaborationManager from '../remoteActivity/liveCollaboration/LiveCollaborationManager';
import LiveCursorTracker from '../remoteActivity/liveCollaboration/cursorTracking/LiveCursorTracker';
import LiveCursorDisplay from '../remoteActivity/liveCollaboration/cursorTracking/LiveCursorDisplay';

import DebugCanvasDocOrigin from './zoom/debug/DebugCanvasDocOrigin';

// Constants
import { InboxState } from '../inbox/inboxConstants';
import { EXPORT_ROOT_ELEMENT_ID } from '../workspace/export/exportConstants';
import { WORKSPACE_SECTIONS } from '../workspace/workspaceConstants';
import { CURSOR_TYPES } from '../../common/users/liveCursor/liveCursorConstants';
import { DEBUG_TOOLS } from '../debug/store/debugToolConstants';
import { ZOOM_MODES } from './zoom/zoomConstants';
import { CANVAS_VIEWPORT_ID } from './utils/canvasConstants';

// Styles
import './Canvas.scss';
import './CanvasDocumentMinSize.scss';
import './ios-board-crash-overrides.scss';
import { iPadAllowDoubleClickCreateShortcutData } from '../components/events/allowDoubleClickCreateShortcutConstants';

const pulseUseTemplateButton = (event) => {
    event.preventDefault();
    triggerBackgroundPulseCssAnimation('.BoardTemplatePickerFooter .use-template-button');
};

/**
 * Creates a 3D matrix transformation to account for the zoom translation px and the scale of the canvas document.
 */
const getCanvasDocumentStyle = ({ canvasSize, zoomScale, zoomTranslationPx }) => {
    let matrix = Rematrix.identity();
    matrix = matrixLib.setScale(matrix, zoomScale);
    matrix = matrixLib.setTranslation(matrix, zoomTranslationPx);

    return {
        ...canvasSize,
        ...convertMatrixToCssTransform(matrix),
    };
};

/* Redux property connectors. */
const mapStateToProps = createSelector(
    getGridSize,
    canvasZoomModeSelector,
    canvasZoomScaleSelector,
    canvasZoomTranslationPxPojoSelector,
    getIsCurrentVisibleBoardFetched,
    getCurrentBoardInboxState,
    getCurrentVisibleBoardCanvasOrigin,
    getPlatformDetailsSelector,
    debugToolProperty(DEBUG_TOOLS.ZOOM, 'documentView'),
    (
        gridSize,
        zoomMode,
        zoomScale,
        zoomTranslationPx,
        isCurrentBoardFetched,
        currentBoardInboxState,
        canvasOrigin,
        platformDetails,
        debugShowDocumentView,
    ) => ({
        gridSize,
        zoomMode,
        zoomScale,
        zoomTranslationPx,
        inboxState: (isCurrentBoardFetched && currentBoardInboxState) || InboxState.CLOSED,
        canvasOrigin,
        platformDetails,
        debugShowDocumentView,
    }),
);

const mapDispatchToProps = (dispatch) => ({
    dispatchSetFocusSection: (section) => dispatch(setFocusSection(section)),
    dispatchSetSelectedElements: (args) => dispatch(setSelectedElements(args)),
    dispatchShowGuestAppRegistrationForm: () => dispatch(showGuestAppRegistrationForm()),
    // This is used by the canvasLineEdgeDropTarget
    dispatchGetLineEdgeCanvasDropPosition: (args) => dispatch(getLineEdgeCanvasDropPosition(args)),
});

const enhance = compose(
    grabScrollDecorator,
    connect(mapStateToProps, mapDispatchToProps),
    zoomStateContextProviderDecorator,
    zoomStateContextConsumer,
    canvasDoubleClickShortcutDecorator,
    canvasSizeDecorator,
    canvasDropTargetDecorator,
    canvasLineEdgeDropTarget,
    canvasReturnStateDecorator,
    selectionMarqueeCanvasStateDecorator,
    dontUpdateForKeys(['isHovered', 'isDragging', 'onCanvasResize', 'createShortcutElement']),
);

const Canvas = (props) => {
    const {
        canvasElementIds,
        sortIndexMap,
        connectDropTarget,
        connectLineEdgeDropTarget,
        gridSize,
        rootBoardId,
        currentBoardId,
        currentBoard,
        currentVisibleBoard,
        currentUser,
        drawingModeEnabled,
        isEditable,
        isInboxOpen,
        onMouseDown,
        onMouseUp,
        onDoubleClick,
        grabEnabled,
        grabbing,
        mouseGrabbing,
        deselectAll,
        permissions,
        blockTemplateInteraction,
        createShortcutElement,
        createShortcutCard,
        createShortcutFile,
        createShortcutLink,
        createShortcutImageLink,
        selectionMarqueeActive,
        setSelectionMarqueeActive,
        setShortcutHandlerCanvasViewportDomElement,
        scrollDecoratorCanvasViewportRef,

        zoomMode,
        zoomScale,
        zoomTranslationPx,
        canvasSize,
        canvasOrigin,

        dispatchSetFocusSection,
        dispatchShowGuestAppRegistrationForm,

        debugShowDocumentView,

        isCurrentBoardFetched,
        platformDetails,
    } = props;

    const canvasViewportRef = useRef();
    const canvasDocumentRef = useRef();
    const scrollAreaRef = useRef();
    const performHapticFeedback = usePerformHapticFeedback();

    useEffect(() => {
        measurementsRegistry.registerCanvasViewport(canvasViewportRef.current);

        return () => {
            measurementsRegistry.removeCanvasViewport();
        };
    }, []);

    useIpadDoubleTapFix();

    const setFocusOnCanvas = useCallback(() => {
        dispatchSetFocusSection(WORKSPACE_SECTIONS.CANVAS);
        dispatchShowGuestAppRegistrationForm();
    }, []);

    const shouldHideGrid =
        zoomScale < 1 ||
        zoomMode === ZOOM_MODES.ZOOM_TO_FIT ||
        // If there's any zoom translation then don't show the grid
        zoomTranslationPx.x !== 0 ||
        zoomTranslationPx.y !== 0;

    const canvasClassNames = classNames('CanvasViewport', {
        'selection-marquee-active': selectionMarqueeActive,
        'debug-document-view': debugShowDocumentView,
        'hide-grid': shouldHideGrid,
    });

    const blockerClassNames = classNames('element-grab-blocker', {
        grab: grabEnabled,
        grabbing,
    });

    // Background styles
    const showGrid =
        !isPlatformIframe(platformDetails) && getShowBoardGrid(currentBoard, currentUser) && !blockTemplateInteraction;

    const store = useStore();

    return (
        <div
            id={CANVAS_VIEWPORT_ID}
            className={canvasClassNames}
            data-scrollable
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onClick={setFocusOnCanvas}
            onContextMenu={setFocusOnCanvas}
            // FIXME-ZOOM - Figure out if we should allow double clicks on the viewport or document only
            onDoubleClick={onDoubleClick}
            onDrop={setFocusOnCanvas}
            ref={multiRef(
                [
                    setShortcutHandlerCanvasViewportDomElement,
                    scrollDecoratorCanvasViewportRef,
                    connectDropTarget,
                    canvasViewportRef,
                ],
                // c is null at certain points of the React lifecycle. Not totally sure why, but
                // we shouldn't change the ref for those moments
                { skipNullRef: true },
            )}
        >
            <ZoomCanvas
                isRootBoard={rootBoardId === currentBoardId}
                isInboxOpen={isInboxOpen}
                gridSize={gridSize}
                zoomMode={zoomMode}
                currentBoardId={currentBoardId}
                canvasDocumentRef={canvasDocumentRef}
                canvasViewportRef={canvasViewportRef}
                scrollAreaRef={scrollAreaRef}
                canvasOrigin={canvasOrigin}
                canvasDocumentSize={canvasSize}
                performHapticFeedback={performHapticFeedback}
            />
            <BodyBackgroundColor currentBoard={currentVisibleBoard || currentBoard} />
            <PoiBoardSectionContext.Provider value={WORKSPACE_SECTIONS.CANVAS}>
                <CanvasScrollArea
                    canvasSize={canvasSize}
                    zoomTranslationPx={zoomTranslationPx}
                    zoomMode={zoomMode}
                    zoomScale={zoomScale}
                    scrollAreaRef={scrollAreaRef}
                >
                    <div
                        className="canvas-document"
                        id={EXPORT_ROOT_ELEMENT_ID}
                        ref={multiRef([connectLineEdgeDropTarget, canvasDocumentRef])}
                        style={getCanvasDocumentStyle(props)}
                        {...iPadAllowDoubleClickCreateShortcutData}
                    >
                        <DebugCanvasDocOrigin gridSize={gridSize} />
                        {showGrid && <div className="grid" />}
                        <div>
                            {(grabEnabled || mouseGrabbing) && <div className={blockerClassNames} />}

                            {blockTemplateInteraction && (
                                <div
                                    className="element-grab-blocker template-blocker"
                                    onClick={pulseUseTemplateButton}
                                />
                            )}
                            <CanvasElementOrchestrator
                                key={currentBoardId}
                                store={store}
                                isCurrentBoardFetched={isCurrentBoardFetched}
                                canvasElementIds={canvasElementIds}
                                sortIndexMap={sortIndexMap}
                                gridSize={gridSize}
                                currentBoardId={currentBoardId}
                                permissions={permissions}
                                deselectAll={deselectAll}
                                createShortcutElement={createShortcutElement}
                                createShortcutCard={createShortcutCard}
                                createShortcutFile={createShortcutFile}
                                createShortcutLink={createShortcutLink}
                                createShortcutImageLink={createShortcutImageLink}
                            />
                            <DocumentModeCanvasDebugger gridSize={gridSize} />

                            <LiveCollaborationManager eventListenerNode={canvasDocumentRef.current} />

                            {!drawingModeEnabled && (
                                <>
                                    {canGiveFeedback(permissions) && (
                                        <LiveCursorTracker
                                            eventListenerNode={canvasDocumentRef.current}
                                            cursorType={CURSOR_TYPES.POINTER}
                                        />
                                    )}

                                    <LiveCursorDisplay currentBoardId={currentBoardId} />
                                    <LiveDrawingCanvasRenderer />
                                </>
                            )}

                            <CanvasScaleManager currentBoardId={currentBoardId} canvasSize={canvasSize} />
                        </div>
                    </div>

                    <SelectionMarquee
                        sectionElementIds={canvasElementIds}
                        setSelectionMarqueeActive={setSelectionMarqueeActive}
                        eventListenerNode={scrollAreaRef.current}
                        canvasDocumentNode={canvasDocumentRef.current}
                        zoomScale={zoomScale}
                        zoomTranslationPx={zoomTranslationPx}
                        disabled={!isEditable}
                    />
                </CanvasScrollArea>
            </PoiBoardSectionContext.Provider>
        </div>
    );
};

Canvas.propTypes = {
    rootBoardId: PropTypes.string,
    currentBoardId: PropTypes.string,
    currentBoard: PropTypes.object,
    currentVisibleBoard: PropTypes.object,
    currentUser: PropTypes.object,
    canvasElementIds: PropTypes.array,
    sortIndexMap: PropTypes.object,
    inboxState: PropTypes.string,
    isInboxOpen: PropTypes.bool,
    connectDropTarget: PropTypes.func,
    gridSize: PropTypes.number,
    zoomMode: PropTypes.string,
    zoomScale: PropTypes.number,
    zoomTranslationPx: PropTypes.object,
    permissions: PropTypes.number,
    isOver: PropTypes.bool,
    drawingModeEnabled: PropTypes.bool,
    isEditable: PropTypes.bool,
    isScaled: PropTypes.bool,
    blockTemplateInteraction: PropTypes.bool,
    onCanvasClick: PropTypes.func,
    onCanvasResize: PropTypes.func,
    onMouseDown: PropTypes.func,
    onMouseUp: PropTypes.func,
    onDoubleClick: PropTypes.func,
    deselectAll: PropTypes.func,
    connectLineEdgeDropTarget: PropTypes.func,
    grabEnabled: PropTypes.bool,
    grabbing: PropTypes.bool,
    mouseGrabbing: PropTypes.bool,
    createShortcutElement: PropTypes.func,
    createShortcutCard: PropTypes.func,
    createShortcutFile: PropTypes.func,
    createShortcutLink: PropTypes.func,
    createShortcutImageLink: PropTypes.func,
    canvasSize: PropTypes.object,
    canvasOrigin: PropTypes.object,
    scrollableContainerStyle: PropTypes.object,
    setShortcutHandlerCanvasViewportDomElement: PropTypes.func,
    scrollDecoratorCanvasViewportRef: PropTypes.func,
    setSizeHandlerCanvasNode: PropTypes.func,
    selectionMarqueeActive: PropTypes.bool,
    setSelectionMarqueeActive: PropTypes.func,
    dispatchSetFocusSection: PropTypes.func,
    dispatchShowGuestAppRegistrationForm: PropTypes.func,

    debugShowDocumentView: PropTypes.bool,

    isCurrentBoardFetched: PropTypes.bool,
    platformDetails: PropTypes.object,
};

export default enhance(Canvas);
